/**
 * ============================================================================
 * ProgramCard2 — View
 * ============================================================================
 * Purpose: Presentational program card UI. Renders ViewModel only.
 * ============================================================================
 */

"use client";

import { memo } from "react";
import cn from "clsx";
import Button from "@/revamp/atoms/Button";
import { UnivoImage, UnivoLink } from "@/components/common";
import { IMAGE_ASSETS } from "@/constants/images";
import { addStaticSubdomain } from "../utils";
import LazyloadComponentOnView from "../LazyLoadComponentOnView";
import CollabOverlay from "./CollabOverlay";
import style from "../ProgramCard2.module.scss";
import type { ProgramCard2ViewModel } from "../model/program-card2.types";

function ProgramCard2ViewBase({
  imageUrl,
  urlKey,
  programHeading,
  courseStripText,
  amount,
  displayName,
  duration,
  visibleCollabKeys,
  isProgramList,
  isAffiliatePage,
  id,
  isProgramListPage,
  corporateOfferLabel,
  discountedAmount,
  noOfferMessage,
  industryAlignedValue,
  onExploreClick,
  onCtaClick,
  onBrochureClick,
  stopCardNavigation,
}: ProgramCard2ViewModel) {
  const isCorporateCard = Boolean(corporateOfferLabel);
  const hasCorporateOffer = Boolean(corporateOfferLabel && discountedAmount);
  const cardUI = (
    <div className={cn(style.root, { [style.rootWithOffer]: isCorporateCard })}>
        <div className={style.img_container}>
          <LazyloadComponentOnView
            source={addStaticSubdomain(imageUrl)}
            alt={programHeading}
          />

          <CollabOverlay
            visibleCollabKeys={visibleCollabKeys}
            industryAlignedValue={industryAlignedValue}
          />
        </div>

        {!!courseStripText && (
          <div className={style.prgm_strip}>
            <UnivoImage
              src={IMAGE_ASSETS.programTagStrip}
              width={150}
              height={50}
              sizes="150px"
              alt="tab logo"
              className={style.prgm_strip}
              loading="lazy"
              fetchPriority="low"
            />
            <span className={style.prgm_yellow_strip}>{courseStripText}</span>
          </div>
        )}

        <div className={style.content_container}>
          <h3 className={style.descMob}>{displayName}</h3>
          <div>
            <div className={style.fee_detail}>
              <span className={cn(style.watch, "lining-nums")}>
                <UnivoImage
                  src={IMAGE_ASSETS.watchIconProgramCard}
                  alt="watch image"
                  width={30}
                  height={30}
                  sizes="30px"
                  loading="lazy"
                  fetchPriority="low"
                />
                {duration}
              </span>
              {!!amount && (
                <span className={cn(style.amount, "lining-nums")}>
                  <UnivoImage
                    src={IMAGE_ASSETS.amountIconProgramCard}
                    alt="amount image"
                    width={30}
                    height={30}
                    sizes="30px"
                    loading="lazy"
                    fetchPriority="low"
                  />
                  {amount}
                </span>
              )}
            </div>
            {hasCorporateOffer ? (
              <div className={style.offerBox}>
                <span className={style.offerLabel}>{corporateOfferLabel}</span>
                <span className={style.offerAmount}>{discountedAmount}</span>
              </div>
            ) : isCorporateCard && noOfferMessage ? (
              <div className={style.offerBox}>
                <span className={style.offerLabel}>{corporateOfferLabel}</span>
                <span className={style.offerAmount}>{noOfferMessage}</span>
              </div>
            ) : null}
            {isAffiliatePage && (
              <Button
                buttonText="Apply Now"
                className={style.button}
                onClick={onExploreClick}
              />
            )}
          </div>
          {isCorporateCard ? (
            <div className={style.corporateCta}>
              <Button
                buttonText="Download Brochure →"
                className={style.button}
                onClick={onBrochureClick}
                id={id}
              />
            </div>
          ) : !isAffiliatePage &&
            (isProgramList && !isProgramListPage ? (
              <div onClick={stopCardNavigation}>
                <Button
                  buttonText="Get Brochure"
                  className={style.button}
                  onClick={onCtaClick}
                  id={id}
                />
              </div>
            ) : (
              <Button
                buttonText="Explore More"
                className={style.button}
                onClick={onCtaClick}
              />
            ))}
        </div>
    </div>
  );

  // Corporate v5: only the brochure CTA is interactive — no whole-card navigation.
  if (isCorporateCard) {
    return (
      <div className={cn(style.card_link, style.card_link_disabled)}>
        {cardUI}
      </div>
    );
  }

  if (isAffiliatePage) {
    return <div className={style.card_link}>{cardUI}</div>;
  }

  if (isProgramListPage) {
    return (
      <div className={cn(style.card_link, style.card_link_disabled)}>
        {cardUI}
      </div>
    );
  }

  return (
    <UnivoLink
      href={urlKey || "#"}
      target="_self"
      id={urlKey}
      aria-label={`Explore-${programHeading}`}
      className={style.card_link}
    >
      {cardUI}
    </UnivoLink>
  );
}

const ProgramCard2View = memo(ProgramCard2ViewBase);
ProgramCard2View.displayName = "ProgramCard2View";

export default ProgramCard2View;
